import { useApolloClient } from '@apollo/react-hooks';
import { type ErrorPolicy, type ApolloClient, ApolloError } from 'apollo-client';
import type { DocumentNode } from 'graphql';
import { useDeepMemo } from '@atlassian/jira-business-common/src/common/utils/use-deep-memo/index.tsx';
import { QueryError } from '@atlassian/jira-business-error-handling/src/utils/query-error/index.tsx';
import { AuthenticationError } from '@atlassian/jira-standard-errors/src/common/utils/authentication-error/index.tsx';
import type { AGGCompliantList, AGGCompliantAPI, ListConnection } from './types.tsx';

export type UseFetchMoreOptions<TData, TVariables> = {
	after: string;
	onError?: (arg1: ApolloError) => void;
	errorPolicy?: ErrorPolicy;
	updateQuery: (
		prev: TData,
		options: {
			fetchMoreResult?: TData;
			variables: TVariables;
		},
	) => TData;
};

type FetchMoreOptions<TData, TVariables> = UseFetchMoreOptions<TData, TVariables> & {
	variables: TVariables;
	query: DocumentNode;
	client: ApolloClient<TData>;
};

/**
 * This implement a similar algorithm to the native fetchMore. We need to implement our own method as Apollo Client 2.*
 * implementation causes some memory leaks and performance degradation in the `ListView`, for more info: https://jbusiness.atlassian.net/browse/JET-2389
 * @param after
 * @param onError
 * @param updateQuery
 * @param query
 * @param variables
 * @param client
 * @returns {*}
 */
export function fetchMore<TData, TVariables>({
	after,
	onError,
	errorPolicy = 'none',
	updateQuery,
	query,
	variables,
	client,
}: FetchMoreOptions<TData, TVariables>) {
	const variablesWithAfter = {
		...variables,
		after,
	};

	return client
		.query({
			query,
			variables: variablesWithAfter,
			fetchPolicy: 'no-cache',
			errorPolicy,
		})
		.then((response) => {
			if (!response) {
				return;
			}

			if (response.errors) {
				onError &&
					onError(
						new ApolloError({
							graphQLErrors: response.errors,
						}),
					);

				if (errorPolicy === 'none') {
					return;
				}
			}

			if (response.data == null) {
				return;
			}

			// Update cache based on updateQuery method
			const prevData = client.readQuery<TData, TVariables>({
				query,
				variables,
			});

			if (prevData == null) {
				// It should not happened, adding the check just to make flow happpy
				return;
			}

			const newData = updateQuery(prevData, {
				fetchMoreResult: response.data,
				variables: variablesWithAfter,
			});

			client.writeQuery({
				query,
				variables,
				// @ts-expect-error - TS2345 - Argument of type '{ query: DocumentNode; variables: TVariables; notifyOnNetworkStatusChange: boolean; data: TData; }' is not assignable to parameter of type 'WriteQueryOptions<TData, TVariables>'.
				notifyOnNetworkStatusChange: true,
				data: newData,
			});
		})
		.catch((err) => {
			onError && onError(err);
		});
}

export function useFetchMore<TData, TVariables>({
	variables,
	query,
}: {
	variables: TVariables;
	query: DocumentNode;
}): (options: UseFetchMoreOptions<TData, TVariables>) => Promise<void> {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const client = useApolloClient() as unknown as ApolloClient<TData>;

	// We're using `useDeepMemo` instead of `useCallback`/`useMemo` as it's very
	// likely that the `variables` dependency will change with each render even
	// though its contents will remain the same. Doing this stops us from
	// unnecessarily returning a new callback, as this could trigger unnecessary
	// rerenders or side-effects.
	return useDeepMemo(
		() => (options: UseFetchMoreOptions<TData, TVariables>) =>
			fetchMore({ ...options, variables, query, client }),
		[client, variables, query],
	);
}

export function getAGGCompliantData<TData extends AGGCompliantAPI, TNode = object>(
	data?: TData | null,
	listName?: keyof AGGCompliantList,
	onError?: (arg1: ApolloError) => void,
): ListConnection<TNode> | null | undefined {
	if (listName && data?.jira?.[listName]) {
		const _data = data.jira[listName];

		if (_data?.__typename === 'QueryError') {
			const queryError = new QueryError(_data);

			if (queryError.extensions?.find((ex) => ex.statusCode === 401)) {
				throw new AuthenticationError(_data.message);
			}

			if (onError) {
				onError(_data);
				return undefined;
			}
			throw new QueryError(_data);
		}

		return _data;
	}

	return undefined;
}
